import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScoreGauge from "@/components/ScoreGauge";
import WhyTrustPanel from "@/components/WhyTrustPanel";
import ScoreExplanationModal from "@/components/ScoreExplanationModal";
import { InfoTooltip } from "@/components/InfoTooltip";
import { scoreToBand } from "@/lib/scoring";

/**
 * Composite-score card. Encapsulates the readiness-score heading, optional
 * (provisional) label when the user has open disputes, the gauge itself, the
 * score band caption, the "What does this score mean?" modal trigger, and
 * the why-trust panel.
 *
 * The pill below the heading clearly distinguishes Estimated (Quick path,
 * conservative worst-case-per-state math) from Verified (Exact-per-state
 * inputs). Estimated scores cannot be publicly shared via the badge.
 */
export default function ScoreCard({ score, openDisputes = 0, scoreMode = "estimated", storeUrlUnverified = false, engineVersion = null, auditDate = null }) {
  const isEstimated = (scoreMode || "estimated").toLowerCase() !== "verified";
  const relativeAge = React.useMemo(() => {
    if (!auditDate) return null;
    try {
      const then = new Date(auditDate).getTime();
      if (Number.isNaN(then)) return null;
      const diffMs = Date.now() - then;
      if (diffMs < 0) return "just now";
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      if (days < 30) return `${days}d ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months}mo ago`;
      return `${Math.floor(months / 12)}y ago`;
    } catch { return null; }
  }, [auditDate]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 flex flex-col items-center justify-center"
      data-testid="dashboard-score-card"
    >
      <div className="flex items-center gap-1 mb-1 flex-wrap justify-center">
        <span className="text-xs uppercase tracking-[0.2em] text-[var(--rg-muted)] font-bold">
          Readiness Score
        </span>
        {isEstimated ? (
          <span
            className="ml-1 text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-bold border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] px-1.5 py-0.5 rounded-sm"
            data-testid="score-mode-estimated"
            title="Estimated score. Exact activity data not provided for this jurisdiction. Provide your exact revenue and transaction figures in your audit inputs to calculate a verified score for this state."
          >
            Estimated
          </span>
        ) : (
          <span
            className="ml-1 text-[10px] uppercase tracking-wider text-[var(--rg-sage)] font-bold border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] px-1.5 py-0.5 rounded-sm"
            data-testid="score-mode-verified"
            title="Verified — exact per-state numbers provided"
          >
            Verified
          </span>
        )}
        {openDisputes > 0 && (
          <span
            className="ml-1 text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-bold border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] px-1.5 py-0.5 rounded-sm"
            data-testid="provisional-label"
          >
            (provisional)
          </span>
        )}
        {/* Tier-3 graceful-degradation badge — only when storeUrlUnverified=true. */}
        {storeUrlUnverified && (
          <span
            className="ml-1 text-[10px] uppercase tracking-wider text-[var(--rg-amber)] font-bold border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] px-1.5 py-0.5 rounded-sm"
            data-testid="store-url-unverified-badge"
            title="We could not automatically confirm this is an active Shopify store. Your score is still based on the information you provided."
          >
            Store URL Unverified
          </span>
        )}
        <InfoTooltip
          testId="tooltip-readiness-score"
          text="A 0-100 indicator of how well your store profile aligns with publicly available regulatory reference points across your selected jurisdictions."
        />
      </div>
      <ScoreGauge score={score} testId="dashboard-composite-gauge" label="" />
      <div className="mt-3 text-sm text-center text-[var(--rg-text)] italic">
        {scoreToBand(score)}
      </div>
      {(engineVersion !== null || relativeAge) && (
        <div
          className="mt-2 text-[10px] font-mono text-[var(--rg-muted)] tracking-wider text-center"
          data-testid="score-engine-version"
          title="Scoring engine version this score was computed under, plus how long ago. If we ship a scoring change, opening the dashboard refreshes automatically for free."
        >
          {engineVersion !== null && <span>Scored under v{engineVersion}</span>}
          {engineVersion !== null && relativeAge && <span className="mx-1">·</span>}
          {relativeAge && <span>{relativeAge}</span>}
        </div>
      )}
      <ScoreExplanationModal />
      {isEstimated && (
        <div
          className="mt-3 w-full rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 text-center"
          data-testid="estimated-mode-cta"
        >
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-amber)] mb-1">
            Quick Estimate Mode
          </div>
          <p className="text-xs text-[var(--rg-text)] mb-2">
            This score uses tier ranges (worst-case per state). Public sharing is
            disabled until you provide exact per-state numbers.
          </p>
          <Link
            to="/settings"
            className="inline-block text-xs font-semibold text-[var(--rg-sage)] hover:underline"
            data-testid="estimated-mode-refine-link"
          >
            Refine for a Verified Score →
          </Link>
        </div>
      )}
      {openDisputes > 0 && (
        <div
          className="mt-2 text-[10px] text-[var(--rg-amber)] text-center font-mono"
          data-testid="provisional-explainer"
        >
          {openDisputes} finding{openDisputes === 1 ? "" : "s"} flagged for review — score remains provisional until resolved.
        </div>
      )}
      <WhyTrustPanel
        testId="dashboard-composite-why"
        formulaExplanation="Your score combines four areas: Sales Tax Nexus (35%), Privacy Law Readiness (30%), Policy Documents (20%), and Jurisdiction Awareness (15%). Each area is scored against verified government sources listed in our Methodology."
      />
    </motion.div>
  );
}
